import { FantazFailure } from '@fantaz/shared/data-access/models';
/**
 * Signature of the function passed as callback in {@link addLeaveActionListener}
 *
 * @param actionId     the ID for which the leave is called
 * @param stoptime     start resp. endtime of the action
 * @param isRootAction true if the action with the provided ID is a root action
 */
export interface ActionLeaveListener {
  (actionId: number, stoptime: number, isRootAction: boolean): void;
}

/**
 * Signature of the function passed as callback in {@link addEnterActionListener}
 *
 * @param actionId     the ID for which the enter is called
 * @param starttime    start resp. endtime of the action
 * @param isRootAction true if the action with the provided ID is a root action
 * @param [element]    the element which resulted in the initiation of the event
 */
export interface ActionEnterListener {
  (
    actionId: number,
    starttime: number,
    isRootAction: boolean,
    element?: EventTarget | string,
  ): void;
}

/**
 * Signature of new the view to start.
 *
 * @param name  New view name. Usually it matches the location.pathname or location.hash
 * @param group New view group. It is recommended to contain the dynamic placeholders of the view name.
 *              For example, if the view name is "/books/123", view group should be "books/:bookId" or similar.
 */
export interface APIPage {
  name: string;
  group: string;
}

// ts-prune-ignore-next Ignored because it is not used, but should still show up in typedoc
export type PageLeaveListener = (unloadRunning: boolean) => void;

export type AllowedMapTypes = Date | string | number;

// this is used for dtrum doc
// eslint-disable-next-line @typescript-eslint/naming-convention -- dtrum is fine here

export interface Property<S> {
  value: S;
  public?: boolean;
}

export interface PropertyMap<S extends AllowedMapTypes> {
  [key: string]: Property<S> | S;
}

export interface FailedProperty {
  key: string;
  reason: string;
}

export interface SuccessfulProperty {
  key: string;
  value: AllowedMapTypes;
}

export interface PropertiesSendingReport {
  failedProperties: FailedProperty[];
  sentProperties: SuccessfulProperty[];
  info: string;
}

export interface PropertyObject {
  javaLong?: PropertyMap<number>;
  date?: PropertyMap<Date>;
  shortString?: PropertyMap<string>;
  javaDouble?: PropertyMap<number>;
}

/**
 * Defines the type of resources for the summary
 */
export const enum ResourceSummaryTypes {
  /** Stylesheet resource (e.g. .css) */
  CSS = 'c',
  /** Custom resource */
  CUSTOM = 'y',
  /** Image resource (e.g. .jpg, .png) */
  IMAGES = 'i',
  /** Undefined resource */
  OTHER = 'o',
  /** Script resource (e.g. .js) */
  SCRIPTS = 's',
}

/**
 * Provides information about call results to actionName.
 */
export const enum ActionNameResult {
  /** Action naming was successful with the provided name */
  SUCCESS = 0,
  /** The action with the provided ID was not found, or there was no currently active action */
  ACTION_NOT_FOUND = 1,
  /** The provided action name was not of type string */
  INVALID_ACTION_NAME = 2,
  /** The provided action ID was provided, not of type number */
  INVALID_ACTION_ID = 3,
}

export interface MetaData {
  /**
   * An internally used id
   *
   * @hidden
   */
  id: string;
  /**
   * Specifies where the metadata is collected from:
   * * CSS Selector
   * * JavaScript Variable
   * * Cookie
   * * Query String
   * * JavaScript Function
   */
  type: string;
  /**
   * How the metadata can be retrieved(cookie name, css selector, javascript variable name, ...)
   */
  expression: string;
  /**
   * The current value for the given expression
   */
  value: string | null;
  /**
   * Shows information about captured value
   */
  info?: string;
}

/**
 * Captures an exception and sends it to PostHog for tracking and analysis.
 *
 * @param event - A string representing the event name (for capture method).
 * @param properties - Optional properties to provide more context about the event.
 * @param userId - A string representing the unique user identifier (for identify method).
 * @param apiKey - A string representing the PostHog API key (for init method).
 * @param config - Optional configuration object for the SDK (for init method).
 * @param error - A string representing the error message or description.
 * @param additionalProperties - Optional additional properties to provide more context about the exception.
 * @param additionalProperties.$exception_fingerprint - A custom string used to group similar exceptions together.
 *   For example, you can use this to assign a custom group identifier for exceptions.
 *   $exception_fingerprint: "MyCustomGroup"
 */
export interface PosthogApi {
  capture: (event: string, properties?: any) => void;
  identify: (distinctId: string, properties?: any) => void;
  init: (apiKey: string, options?: any) => void;
  captureException(
    error: string,
    additionalProperties?: {
      key?: string;
      value?: string;
      hint?: string;
      parentingInfo?: number | boolean;
      $exception_fingerprint?: string;
    },
  ): void;
}

declare global {
  interface Window {
    posthog?: PosthogApi;
  }
}

class TrackError {
  /**
   * Reports an error object to Dynatrace. Use when you catch errors in your own application code
   * but you also want to propagate them to Dynatrace instead of logging them yourself.
   *
   * @param error          The error to be reported. Any browser error object is supported and if the error doesn't
   *                       have a stacktrace the RUM JavaScipt monitoring code will attempt to generate one.
   *                       Alternatively create your own object that has the following properties set: 'message',
   *                       'file', 'line', 'column', and 'stack'. The 'message' property must be provided; all other values are optional.
   * @param parentActionId parent action id. if not passed or null, error is added to current action
   */
  static trackError(error: Error | FantazFailure) {
    console.group('[Core/Dynatrace] Send error to Dynatrace');
    console.log('Error:', error);
    const formattedError = {
      key: 'Error',
      value: error?.message,
      hint: error?.stack,
      $exception_fingerprint: (error as FantazFailure)?.errorGroup,
    };
    window?.posthog?.captureException(error?.message, formattedError);
    console.groupEnd();
  }

  /**
   * Report your own error to dynatrace.
   * For example, use when you want to capture form validation errors in your signup process.
   * Custom errors must first be defined in the Application settings.
   *
   * @param key           The key of the error. For example, 'validation error'
   * @param value         The error value. For example, 'Email validation failed'
   * @param hint          A hint to pinpoint the problem, e.g. content of the input element which triggered the failed validation
   * @param parentingInfo How the custom error should be attached (default = false),
   *                      [case number]: To which open action the custom error event should be attached,
   *                      [case boolean]: If true it will get attached to the current active action
   */
  static trackCustomError(
    key: string,
    value: string,
    hint?: string,
    parentingInfo?: number | boolean,
    $exception_fingerprint?: string,
  ) {
    console.group('[Core/Dynatrace] Send custom error to Dynatrace');
    console.log('key:', key);
    console.log('value:', value);
    console.log('hint:', hint);
    console.log('parentingInfo:', parentingInfo);
    window?.posthog?.captureException(value, {
      key,
      hint,
      parentingInfo,
      $exception_fingerprint,
    });
    console.groupEnd();
  }
}

export { TrackError };
